import { type FC, useState } from "react"
import styled from "styled-components"

import { useFile } from "@forento/ui/hooks/useFile"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import ImageUploader from "~/components/ImageUploader"
import InputField, { useRichTextArea } from "~/components/InputField"
import {
	DefaultModal,
	ModalButtons,
	ModalContentPadding,
	ModalHeader,
	ModalHelpCenter,
	ModalHelpCenterIcon,
	ModalTitle,
} from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/UserContext"
import trpc from "~/utilities/trpc"

type Data = {
	title: string
	shortDescription: string
	isMembersLimited: boolean
	maxMembers: string
	monthlyPricing: { isEnabled: boolean; value: number }
	annuallyPricing: { isEnabled: boolean; value: number }
}

type Props = { isOpen: boolean; onClose(): void; onSubmitted(): Promise<unknown> }
const CreateModal: FC<Props> = ({ isOpen, onClose, onSubmitted }) => {
	const platform = usePlatform()!
	const alert = useAlert()

	const [data, setData] = useState<Data>({
		title: "",
		shortDescription: "",
		isMembersLimited: false,
		maxMembers: "10",
		monthlyPricing: { isEnabled: false, value: 0 },
		annuallyPricing: { isEnabled: false, value: 0 },
	})
	const longDescription = useRichTextArea({ label: "Description" })
	const [isSubmitting, setSubmitting] = useState(false)

	const thumbnail = useFile(-1, null)

	function setField<T>(field: keyof Data): (value: T) => void {
		return value => setData(current => ({ ...current, [field]: value }))
	}

	async function handleSubmit() {
		const maxMembers = data.isMembersLimited ? Number(data.maxMembers) : null
		if (data.title.length === 0) {
			alert.show("Error", "Please fill in the name.")
			return
		}
		if (maxMembers !== null && (Number.isNaN(maxMembers) || maxMembers < 0)) {
			alert.show("Error", "Please fill in a valid number of max members.")
			return
		}
		if (!data.monthlyPricing.isEnabled && !data.annuallyPricing.isEnabled) {
			alert.show("Error", "You need at least one pricing plan.")
			return
		}
		if (
			(data.monthlyPricing.isEnabled && data.monthlyPricing.value <= 0) ||
			(data.annuallyPricing.isEnabled && data.annuallyPricing.value <= 0)
		) {
			alert.show(
				"Free membership",
				'You don\'t need to create any free membership tier. If you want to offer free content to users of your platform, you can publish products as "Free" or "Signed in". This will allow your users to access content without going through any sort of payment gateway checkout. You can learn more here: [🛒 Products on Forento](https://forento.link/membership-modal-products) & [📦 Memberships](https://forento.link/membership-modal-memberships).',
			)
			return
		}
		if (
			data.monthlyPricing.isEnabled &&
			data.annuallyPricing.isEnabled &&
			data.annuallyPricing.value > data.monthlyPricing.value * 12
		) {
			const dialog = await alert.confirm(
				"Price mismatch",
				"Your annual plan is more expensive than your monthly plan. Are you sure this is intentional?",
				{ confirmLabel: "Continue", isDestructive: false },
			)
			if (!dialog.result) return
			dialog.close()
		}

		setSubmitting(true)

		try {
			await trpc.membership.create.mutate({
				title: data.title,
				shortDescription: data.shortDescription,
				longDescription: await longDescription.exportEditorState(),
				thumbnailDataUrl: await thumbnail.exportToDataUrl(),
				maxMembers,
				price: {
					month: data.monthlyPricing.isEnabled ? data.monthlyPricing.value : null,
					year: data.annuallyPricing.isEnabled ? data.annuallyPricing.value : null,
				},
			})

			await onSubmitted()
			onClose()
			setData({
				title: "",
				shortDescription: "",
				isMembersLimited: false,
				maxMembers: "10",
				monthlyPricing: { isEnabled: false, value: 0 },
				annuallyPricing: { isEnabled: false, value: 0 },
			})
			longDescription.clear()
			thumbnail.clear()
		} catch (error) {
			console.error(error)
			setSubmitting(false)
		}
		setSubmitting(false)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen} width={800} onSubmit={handleSubmit}>
			<ModalHeader>
				<ModalTitle>Create a new tier</ModalTitle>
				<ModalHelpCenter newTab onClick="https://forento.link/create-membership">
					<ModalHelpCenterIcon />
					Learn more
				</ModalHelpCenter>
			</ModalHeader>
			<ModalContentPadding>
				<Section>
					<SectionTitle>Basic information</SectionTitle>
					<InputField label="Name" value={data.title} onChange={setField("title")} />
					<InputField
						label="Short description"
						value={data.shortDescription}
						onChange={setField("shortDescription")}
					/>
					{longDescription.element}
					<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
				</Section>

				<Section>
					<SectionTitle>Member limits</SectionTitle>
					<Checkbox
						label="Limit the number of members"
						isChecked={data.isMembersLimited}
						onChange={value => setField("isMembersLimited")(value)}
					/>
					{data.isMembersLimited && (
						<InputField
							label="Max members"
							inputType="number"
							value={data.maxMembers}
							onChange={setField("maxMembers")}
						/>
					)}
				</Section>

				<Section>
					<SectionTitle>Pricing plans</SectionTitle>
					<PricingContainer>
						<PricingCard>
							<Checkbox
								label="Offer monthly plan"
								isChecked={data.monthlyPricing.isEnabled}
								onChange={value =>
									setField("monthlyPricing")({ ...data.monthlyPricing, isEnabled: value })
								}
								className="mb-3"
							/>
							<InputField
								label={`Price per month (${platform.currency})`}
								inputType="number"
								value={data.monthlyPricing.value.toString()}
								onChange={value =>
									setField("monthlyPricing")({ ...data.monthlyPricing, value: Number(value) })
								}
								disabled={!data.monthlyPricing.isEnabled}
							/>
						</PricingCard>
						<PricingCard>
							<Checkbox
								label="Offer annual plan"
								isChecked={data.annuallyPricing.isEnabled}
								onChange={value =>
									setField("annuallyPricing")({ ...data.annuallyPricing, isEnabled: value })
								}
								className="mb-3"
							/>
							<InputField
								label={`Price per year (${platform.currency})`}
								inputType="number"
								value={data.annuallyPricing.value.toString()}
								onChange={value =>
									setField("annuallyPricing")({ ...data.annuallyPricing, value: Number(value) })
								}
								disabled={!data.annuallyPricing.isEnabled}
							/>
						</PricingCard>
					</PricingContainer>
				</Section>

				<Section>
					<SectionTitle>Included content</SectionTitle>
					<EmptyContentState>
						<div className="mb-4 text-5xl">📚</div>
						<p className="text-light text-sm">Add content from the product's pricing page</p>
					</EmptyContentState>
				</Section>
			</ModalContentPadding>
			<ModalButtons>
				<Button variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<SubmitButton variant="primary" isLoading={isSubmitting}>
					Create tier
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

const Section = styled.div`
	margin-bottom: 32px;
	padding-bottom: 24px;
	border-bottom: 1px solid #f0f0f0;
	display: flex;
	flex-direction: column;
	gap: 16px;

	&:last-child {
		margin-bottom: 0;
		padding-bottom: 0;
		border-bottom: none;
	}
`

const SectionTitle = styled.h3`
	font-weight: 600;
	font-size: 16px;
	color: #1f2937;
	display: flex;
	align-items: center;
	gap: 8px;

	&::before {
		content: "";
		width: 4px;
		height: 16px;
		background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
		border-radius: 2px;
	}
`

const PricingContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	margin-bottom: 12px;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
		gap: 16px;
	}
`

const PricingCard = styled.div`
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 20px;
	transition: all 0.2s ease;

	&:hover {
		border-color: #cbd5e1;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	}
`

const EmptyContentState = styled.div`
	background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	border: 2px dashed #cbd5e1;
	border-radius: 16px;
	padding: 40px 24px;
	text-align: center;
	transition: all 0.2s ease;

	&:hover {
		border-color: #94a3b8;
		background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
	}
`

export default CreateModal
